import React, { useState, useEffect } from 'react';
import { supabaseAdmin as supabase } from '../../lib/supabase.js';

const tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3';

const TestTimetableData = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const testConnection = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('Testing database connection...');

      // Test 1: Check if table exists
      const { data: tableTest, error: tableError } = await supabase
        .from('saas_teacher_timetables')
        .select('*')
        .limit(1);

      if (tableError) {
        setError(`Table Error: ${tableError.message}`);
        setMessage('❌ Table does not exist or access denied');
        return;
      }

      setMessage('✅ Table exists and is accessible');

      // Test 2: Count total records
      const { count, error: countError } = await supabase
        .from('saas_teacher_timetables')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId);

      if (countError) {
        setError(`Count Error: ${countError.message}`);
        setMessage('❌ Error counting records');
        return;
      }

      setMessage(`✅ Found ${count} records for your tenant`);

      // Test 3: Load sample data
      const { data: sampleData, error: sampleError } = await supabase
        .from('saas_teacher_timetables')
        .select('*')
        .eq('tenant_id', tenantId)
        .limit(5);

      if (sampleError) {
        setError(`Sample Error: ${sampleError.message}`);
        setMessage('❌ Error loading sample data');
        return;
      }

      setData(sampleData || []);
      setMessage(`✅ Successfully loaded ${sampleData?.length || 0} sample records`);

    } catch (err) {
      console.error('Test error:', err);
      setError(`Connection Error: ${err.message}`);
      setMessage('❌ Failed to connect to database');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    testConnection();
  }, []);

  return (
    <div className="test-timetable-data bg-white rounded-lg shadow-md p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
        <span className="mr-2">🧪</span>
        Database Connection Test
      </h3>

      {/* Test Results */}
      <div className="mb-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h4 className="font-medium text-gray-700 mb-3">Test Results</h4>
        <div className="space-y-2">
          <div className="flex items-center">
            <span className="mr-2">
              {loading ? '⏳' : message.includes('✅') ? '✅' : '❌'}
            </span>
            <span className="text-sm">{message || 'Ready to test...'}</span>
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
      </div>

      {/* Test Button */}
      <div className="mb-4">
        <button
          onClick={testConnection}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'Testing...' : 'Run Test Again'}
        </button>
      </div>

      {/* Sample Data Display */}
      {data.length > 0 && (
        <div className="mb-4">
          <h4 className="font-medium text-gray-700 mb-3">Sample Data (First 5 Records)</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Teacher Name</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Day</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Time</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Subject</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Class</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm font-medium">Room</th>
                </tr>
              </thead>
              <tbody>
                {data.map((record, index) => (
                  <tr key={record.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-2 py-1 text-xs">{record.teacher_name || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs">{record.day_of_week || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs">{record.time_slot || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs">{record.subject || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs">{record.class_id || 'N/A'}</td>
                    <td className="border border-gray-300 px-2 py-1 text-xs">{record.classroom || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-700 mb-2">What This Test Does:</h4>
        <ul className="text-sm text-blue-600 space-y-1">
          <li>• <strong>Tests database connection</strong> to Supabase</li>
          <li>• <strong>Checks if table exists</strong> and is accessible</li>
          <li>• <strong>Counts total records</strong> for your tenant</li>
          <li>• <strong>Loads sample data</strong> to verify data structure</li>
          <li>• <strong>Shows any errors</strong> that need to be fixed</li>
        </ul>
        <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Next Step:</strong> If this test passes, you can use the full Debug Data Viewer to analyze all your timetable data.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TestTimetableData;
